/**
 * Interface Designer — user-composable dashboards.
 *
 * View mode: renders widgets on a 12-column grid.
 * Edit mode (when ?edit=1): add/remove widgets, configure, save.
 *
 * Widget types: scorecard-grid, entity-list, metric-tile, iframe, markdown, chart.
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  LayoutGrid, Plus, Save, Trash2, Pencil, Eye, RefreshCw, ChevronDown,
  AlertCircle, X, Award, Boxes, Gauge, Code, FileText, BarChart3, GripVertical,
} from 'lucide-react'
import { clsx } from 'clsx'
// react-grid-layout v2.2+ exports a pre-composed `ResponsiveGridLayout`
// which already wraps Responsive with WidthProvider — no need to compose.
// Types from @types/react-grid-layout v1 don't match v2.2's prop shape,
// so we widen the component to `any` just at the import boundary.
import { ResponsiveGridLayout as _ResponsiveGridLayout } from 'react-grid-layout'
const GridLayout: any = _ResponsiveGridLayout
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { api } from '../lib/api'

interface RGLLayoutItem {
  i: string
  x: number
  y: number
  w: number
  h: number
  minW?: number
  minH?: number
}

interface Widget {
  id: string
  type: string
  title: string
  config: Record<string, any>
  x: number; y: number; w: number; h: number
}

interface Dashboard {
  identifier: string
  title: string
  description: string
  owner_email: string
  is_public: boolean
  widgets: Widget[]
}

const WIDGET_TYPES = [
  { id: 'scorecard-grid', label: 'Scorecard grid',  icon: Award },
  { id: 'entity-list',    label: 'Entity list',     icon: Boxes },
  { id: 'metric-tile',    label: 'Metric tile',     icon: Gauge },
  { id: 'iframe',         label: 'Iframe',          icon: Code },
  { id: 'markdown',       label: 'Markdown',        icon: FileText },
  { id: 'chart',          label: 'Chart',           icon: BarChart3 },
]

export default function DesignerPage() {
  const [search, setSearch] = useSearchParams()
  const editing = search.get('edit') === '1'
  const [dashboards, setDashboards] = useState<Dashboard[]>([])
  const [current, setCurrent] = useState<Dashboard | null>(null)
  const [blueprints, setBlueprints] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState(0)  // force widget reloads

  const loadList = useCallback(async () => {
    setLoading(true)
    try {
      const [dRes, bRes] = await Promise.all([
        api.get('/api/dashboards'),
        api.get('/api/blueprints'),
      ])
      setDashboards(dRes.data.dashboards || [])
      setBlueprints(bRes.data.blueprints || [])
      if (!current && dRes.data.dashboards?.length) {
        setCurrent(dRes.data.dashboards[0])
      }
      setError(null)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Load failed')
    } finally {
      setLoading(false)
    }
  }, [current])

  useEffect(() => { loadList() }, [loadList])

  const selectDash = (d: Dashboard) => {
    setCurrent(d)
  }

  const createNew = () => {
    setCurrent({
      identifier: '',
      title: 'New dashboard',
      description: '',
      owner_email: '',
      is_public: false,
      widgets: [],
    })
    setSearch(p => { p.set('edit', '1'); return p })
  }

  const saveDash = async () => {
    if (!current) return
    if (!/^[a-z][a-z0-9_-]{1,79}$/.test(current.identifier)) {
      setError('identifier must be lowercase slug')
      return
    }
    setSaving(true)
    try {
      const payload = {
        identifier: current.identifier,
        title: current.title,
        description: current.description,
        is_public: current.is_public,
        widgets: current.widgets,
      }
      const existing = dashboards.find(d => d.identifier === current.identifier)
      if (existing) {
        await api.put(`/api/dashboards/${current.identifier}`, payload)
      } else {
        await api.post('/api/dashboards', payload)
      }
      await loadList()
      setSearch(p => { p.delete('edit'); return p })
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const deleteDash = async () => {
    if (!current) return
    if (!confirm(`Delete dashboard ${current.identifier}?`)) return
    try {
      await api.delete(`/api/dashboards/${current.identifier}`)
      setCurrent(null)
      await loadList()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Delete failed')
    }
  }

  const seed = async () => {
    try {
      await api.post('/api/dashboards/seed')
      await loadList()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Seed failed')
    }
  }

  const addWidget = (type: string) => {
    if (!current) return
    const id = `w${Date.now()}`
    const newW: Widget = {
      id, type, title: type,
      config:
        type === 'scorecard-grid' ? { blueprint: blueprints[0]?.identifier || 'service' } :
        type === 'entity-list'    ? { blueprint: blueprints[0]?.identifier || 'service', limit: 10 } :
        type === 'metric-tile'    ? { label: 'CVE count', api_path: '/api/cyber/threat-level', json_path: 'count', suffix: '' } :
        type === 'iframe'         ? { url: 'https://example.com' } :
        type === 'markdown'       ? { body: '## Notes\n\nWrite anything here.' } :
        type === 'chart'          ? { api_path: '', x_key: 'date', y_key: 'value' } :
        {},
      x: 0, y: 0, w: 6, h: 3,
    }
    setCurrent({ ...current, widgets: [...current.widgets, newW] })
  }

  const removeWidget = (id: string) => {
    if (!current) return
    setCurrent({ ...current, widgets: current.widgets.filter(w => w.id !== id) })
  }

  const updateWidget = (id: string, patch: Partial<Widget>) => {
    if (!current) return
    setCurrent({
      ...current,
      widgets: current.widgets.map(w => w.id === id ? { ...w, ...patch } : w),
    })
  }

  return (
    <div className="min-h-screen bg-graphite text-whitesmoke">
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gunmetal">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-3">
              <LayoutGrid className="w-6 h-6 text-sky" />
              Interface Designer
              {current && (
                <span className="text-base font-normal text-whitesmoke/60">
                  · {current.title}
                </span>
              )}
            </h1>
            <p className="text-sm text-whitesmoke/60 mt-1">
              Compose dashboards from widgets — scorecard grids, entity lists, metric tiles, iframes, markdown.
            </p>
          </div>
          <div className="flex gap-2">
            <select
              aria-label="Select dashboard"
              value={current?.identifier || ''}
              onChange={e => {
                const d = dashboards.find(dd => dd.identifier === e.target.value)
                if (d) selectDash(d)
              }}
              className="px-2 py-1.5 bg-gunmetal border border-gunmetal rounded text-sm"
            >
              <option value="">— select dashboard —</option>
              {dashboards.map(d => (
                <option key={d.identifier} value={d.identifier}>{d.title}</option>
              ))}
            </select>
            <button
              onClick={createNew}
              className="px-3 py-1.5 rounded bg-sky/20 hover:bg-sky/30 text-sky text-sm flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> New
            </button>
            <button
              onClick={seed}
              className="px-3 py-1.5 rounded bg-gunmetal hover:bg-gunmetal/80 text-sm flex items-center gap-1"
            >
              <RefreshCw className="w-4 h-4" /> Seed
            </button>
            {current && !editing && (
              <button
                onClick={() => setSearch(p => { p.set('edit', '1'); return p })}
                className="px-3 py-1.5 rounded bg-amber/20 hover:bg-amber/30 text-amber text-sm flex items-center gap-1"
              >
                <Pencil className="w-4 h-4" /> Edit
              </button>
            )}
            {current && editing && (
              <>
                <button
                  onClick={() => { setSearch(p => { p.delete('edit'); return p }); loadList() }}
                  className="px-3 py-1.5 rounded bg-gunmetal hover:bg-gunmetal/80 text-sm flex items-center gap-1"
                >
                  <Eye className="w-4 h-4" /> View
                </button>
                <button
                  onClick={saveDash}
                  disabled={saving}
                  className="px-3 py-1.5 rounded bg-emerald/20 hover:bg-emerald/30 text-emerald text-sm flex items-center gap-1"
                >
                  <Save className="w-4 h-4" /> Save
                </button>
                {dashboards.find(d => d.identifier === current.identifier) && (
                  <button
                    onClick={deleteDash}
                    title="Delete dashboard"
                    aria-label="Delete dashboard"
                    className="px-3 py-1.5 rounded bg-rose/10 hover:bg-rose/20 text-rose text-sm flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-rose/10 border border-rose/30 text-rose text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-rose/60 hover:text-rose text-xs">dismiss</button>
          </div>
        )}

        {!current && (
          <div className="border border-gunmetal rounded p-12 text-center text-whitesmoke/40">
            {loading ? 'Loading…' : 'No dashboard selected. Click Seed or New.'}
          </div>
        )}

        {current && editing && (
          <div className="mb-4 border border-amber/30 rounded p-4 bg-amber/5">
            <div className="text-xs uppercase tracking-wider text-amber mb-3">Editing — settings</div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <label className="block">
                <span className="text-xs text-whitesmoke/60">Identifier</span>
                <input
                  value={current.identifier}
                  onChange={e => setCurrent({ ...current, identifier: e.target.value })}
                  disabled={!!dashboards.find(d => d.identifier === current.identifier)}
                  className="w-full mt-1 px-2 py-1.5 bg-graphite border border-gunmetal rounded text-sm font-mono"
                />
              </label>
              <label className="block">
                <span className="text-xs text-whitesmoke/60">Title</span>
                <input
                  value={current.title}
                  onChange={e => setCurrent({ ...current, title: e.target.value })}
                  className="w-full mt-1 px-2 py-1.5 bg-graphite border border-gunmetal rounded text-sm"
                />
              </label>
              <label className="flex items-end gap-2 pb-2">
                <input
                  type="checkbox"
                  checked={current.is_public}
                  onChange={e => setCurrent({ ...current, is_public: e.target.checked })}
                />
                <span className="text-sm">Visible to all users</span>
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              {WIDGET_TYPES.map(t => {
                const Icon = t.icon
                return (
                  <button
                    key={t.id}
                    onClick={() => addWidget(t.id)}
                    className="px-3 py-1.5 bg-sky/10 hover:bg-sky/20 text-sky rounded text-xs flex items-center gap-1"
                  >
                    <Icon className="w-3 h-3" /> {t.label}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {current && current.widgets.length === 0 && (
          <div className="border border-dashed border-gunmetal/50 rounded p-12 text-center text-whitesmoke/40">
            {editing ? 'Add widgets from the buttons above.' : 'No widgets yet — switch to Edit mode.'}
          </div>
        )}
        {current && current.widgets.length > 0 && (
          <GridLayout
            className="layout"
            layouts={{
              lg: current.widgets.map(w => ({ i: w.id, x: w.x, y: w.y, w: w.w, h: w.h, minW: 2, minH: 2 })),
            }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 12, sm: 8, xs: 4, xxs: 2 }}
            rowHeight={96}
            margin={[12, 12]}
            isDraggable={editing}
            isResizable={editing}
            draggableHandle=".widget-drag-handle"
            onLayoutChange={(layout: readonly RGLLayoutItem[]) => {
              if (!editing || !current) return
              // Map layout back to widgets preserving order; ignore if unchanged.
              const byId: Record<string, RGLLayoutItem> = Object.fromEntries(layout.map(l => [l.i, l]))
              const next = current.widgets.map(w => {
                const l = byId[w.id]
                return l ? { ...w, x: l.x, y: l.y, w: l.w, h: l.h } : w
              })
              // Shallow-compare to avoid infinite loops
              const changed = next.some((w, i) => (
                w.x !== current.widgets[i].x || w.y !== current.widgets[i].y ||
                w.w !== current.widgets[i].w || w.h !== current.widgets[i].h
              ))
              if (changed) setCurrent({ ...current, widgets: next })
            }}
          >
            {current.widgets.map(w => (
              <div
                key={w.id}
                className="border border-gunmetal rounded overflow-hidden bg-gunmetal/10"
              >
                <div className={clsx(
                  'bg-gunmetal/40 px-3 py-1.5 flex items-center justify-between',
                  editing && 'widget-drag-handle cursor-move',
                )}>
                  <span className="text-xs uppercase tracking-wider text-whitesmoke/70 flex items-center gap-1.5">
                    {editing && <GripVertical className="w-3 h-3 text-whitesmoke/40" />}
                    {w.title || w.type}
                  </span>
                  {editing && (
                    <button
                      onClick={() => removeWidget(w.id)}
                      title="Remove widget"
                      aria-label="Remove widget"
                      className="text-rose/70 hover:text-rose"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="h-[calc(100%-28px)] overflow-auto">
                  {editing ? (
                    <WidgetConfig w={w} update={p => updateWidget(w.id, p)} blueprints={blueprints} />
                  ) : (
                    <WidgetView w={w} refreshToken={refreshToken} />
                  )}
                </div>
              </div>
            ))}
          </GridLayout>
        )}
      </div>
    </div>
  )
}

// ── Widget renderer (view mode) ────────────────────────────────────────────

function WidgetView({ w, refreshToken }: { w: Widget; refreshToken: number }) {
  const [data, setData] = useState<any>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    (async () => {
      setErr(null)
      try {
        if (w.type === 'scorecard-grid' && w.config.blueprint) {
          const { data } = await api.get(`/api/scorecards/grid/${w.config.blueprint}`)
          setData(data)
        } else if (w.type === 'entity-list' && w.config.blueprint) {
          const { data } = await api.get(`/api/blueprints/${w.config.blueprint}/entities`, {
            params: { limit: w.config.limit || 10 },
          })
          setData(data)
        } else if (w.type === 'metric-tile' && w.config.api_path) {
          const { data } = await api.get(w.config.api_path)
          setData(data)
        }
      } catch (e: any) {
        setErr(e?.response?.data?.detail || e.message || 'load failed')
      }
    })()
  }, [w.type, JSON.stringify(w.config), refreshToken])

  if (err) return <div className="p-3 text-rose text-xs">Error: {err}</div>

  if (w.type === 'markdown') {
    return (
      <div className="p-3 text-sm whitespace-pre-wrap text-whitesmoke/80">
        {w.config.body || ''}
      </div>
    )
  }
  if (w.type === 'iframe') {
    return (
      <iframe
        src={w.config.url || ''}
        className="w-full h-full border-0"
        sandbox="allow-same-origin allow-scripts"
      />
    )
  }
  if (w.type === 'metric-tile') {
    const val = w.config.json_path
      ? w.config.json_path.split('.').reduce((a: any, k: string) => a?.[k], data)
      : data
    return (
      <div className="p-4 flex flex-col items-center justify-center h-full">
        <div className="text-4xl font-semibold">
          {val ?? '—'}{w.config.suffix || ''}
        </div>
        <div className="text-xs text-whitesmoke/60 mt-1">{w.config.label}</div>
      </div>
    )
  }
  if (w.type === 'scorecard-grid' && data) {
    return (
      <div className="p-2 overflow-auto h-full text-xs">
        <table className="w-full">
          <thead className="text-whitesmoke/60">
            <tr className="border-b border-gunmetal/50">
              <th className="text-left py-1 pr-2">Entity</th>
              {data.scorecards.map((sc: any) => (
                <th key={sc.identifier} className="text-left py-1 pr-2">{sc.title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r: any) => (
              <tr key={r.entity_identifier} className="border-b border-gunmetal/30">
                <td className="py-1 pr-2 font-mono">{r.entity_title}</td>
                {r.cells.map((c: any) => (
                  <td key={c.scorecard_identifier} className="py-1 pr-2">
                    <span className={clsx(
                      'px-1.5 py-0.5 rounded text-[10px] font-bold uppercase',
                      c.level === 'gold' && 'bg-amber/20 text-amber',
                      c.level === 'silver' && 'bg-whitesmoke/10 text-whitesmoke',
                      c.level === 'bronze' && 'bg-orange-500/20 text-orange-400',
                      c.level === 'none' && 'bg-gunmetal text-whitesmoke/40',
                    )}>
                      {c.score}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }
  if (w.type === 'entity-list' && data) {
    const props = w.config.properties_to_show || []
    return (
      <div className="p-2 overflow-auto h-full text-xs">
        <ul className="divide-y divide-gunmetal/30">
          {data.entities.map((e: any) => (
            <li key={e.identifier} className="py-1">
              <div className="font-medium">{e.title}</div>
              {props.length > 0 && (
                <div className="text-whitesmoke/60 mt-0.5">
                  {props.map((p: string) => `${p}=${e.properties?.[p] ?? '—'}`).join(' · ')}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    )
  }
  if (w.type === 'chart') {
    return (
      <div className="p-3 text-xs text-whitesmoke/60">
        Chart widget: {w.config.api_path || '(no api_path)'}. Simple ASCII fallback — hook up recharts for v2.
      </div>
    )
  }
  return <div className="p-3 text-xs text-whitesmoke/40">Widget type {w.type} not rendered.</div>
}

// ── Widget config editor (edit mode) ────────────────────────────────────

function WidgetConfig({
  w, update, blueprints,
}: { w: Widget; update: (p: Partial<Widget>) => void; blueprints: any[] }) {
  const set = (k: string, v: any) => update({ config: { ...w.config, [k]: v } })
  return (
    <div className="p-2 space-y-1.5 text-xs">
      <div className="grid grid-cols-4 gap-1">
        <label className="block">
          <span className="text-whitesmoke/50">title</span>
          <input
            value={w.title}
            onChange={e => update({ title: e.target.value })}
            className="w-full px-1 py-0.5 bg-gunmetal/30 border border-gunmetal rounded"
          />
        </label>
        <label className="block">
          <span className="text-whitesmoke/50">x</span>
          <input type="number" value={w.x} onChange={e => update({ x: +e.target.value })}
            className="w-full px-1 py-0.5 bg-gunmetal/30 border border-gunmetal rounded font-mono" />
        </label>
        <label className="block">
          <span className="text-whitesmoke/50">w</span>
          <input type="number" value={w.w} min={1} max={12} onChange={e => update({ w: +e.target.value })}
            className="w-full px-1 py-0.5 bg-gunmetal/30 border border-gunmetal rounded font-mono" />
        </label>
        <label className="block">
          <span className="text-whitesmoke/50">h</span>
          <input type="number" value={w.h} min={1} onChange={e => update({ h: +e.target.value })}
            className="w-full px-1 py-0.5 bg-gunmetal/30 border border-gunmetal rounded font-mono" />
        </label>
      </div>
      {(w.type === 'scorecard-grid' || w.type === 'entity-list') && (
        <label className="block">
          <span className="text-whitesmoke/50">blueprint</span>
          <select
            value={w.config.blueprint || ''}
            onChange={e => set('blueprint', e.target.value)}
            className="w-full px-1 py-0.5 bg-gunmetal/30 border border-gunmetal rounded"
          >
            <option value="">—</option>
            {blueprints.map(b => <option key={b.identifier} value={b.identifier}>{b.title}</option>)}
          </select>
        </label>
      )}
      {w.type === 'entity-list' && (
        <label className="block">
          <span className="text-whitesmoke/50">properties_to_show (csv)</span>
          <input
            value={(w.config.properties_to_show || []).join(',')}
            onChange={e => set('properties_to_show', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            className="w-full px-1 py-0.5 bg-gunmetal/30 border border-gunmetal rounded"
          />
        </label>
      )}
      {w.type === 'metric-tile' && (
        <>
          <label className="block">
            <span className="text-whitesmoke/50">label</span>
            <input value={w.config.label || ''} onChange={e => set('label', e.target.value)}
              className="w-full px-1 py-0.5 bg-gunmetal/30 border border-gunmetal rounded" />
          </label>
          <label className="block">
            <span className="text-whitesmoke/50">api_path</span>
            <input value={w.config.api_path || ''} onChange={e => set('api_path', e.target.value)}
              className="w-full px-1 py-0.5 bg-gunmetal/30 border border-gunmetal rounded font-mono" />
          </label>
          <label className="block">
            <span className="text-whitesmoke/50">json_path (dot-notation)</span>
            <input value={w.config.json_path || ''} onChange={e => set('json_path', e.target.value)}
              className="w-full px-1 py-0.5 bg-gunmetal/30 border border-gunmetal rounded font-mono" />
          </label>
        </>
      )}
      {w.type === 'iframe' && (
        <label className="block">
          <span className="text-whitesmoke/50">url</span>
          <input value={w.config.url || ''} onChange={e => set('url', e.target.value)}
            className="w-full px-1 py-0.5 bg-gunmetal/30 border border-gunmetal rounded" />
        </label>
      )}
      {w.type === 'markdown' && (
        <label className="block">
          <span className="text-whitesmoke/50">body</span>
          <textarea
            value={w.config.body || ''}
            onChange={e => set('body', e.target.value)}
            rows={4}
            className="w-full px-1 py-0.5 bg-gunmetal/30 border border-gunmetal rounded text-xs"
          />
        </label>
      )}
    </div>
  )
}
